/**
 * Module Partage — contrat du CAS, de sa timeline et de sa fiche d'instructions.
 *
 * Separe de `partage.ts`, qui porte le socle d'identite (types nominaux, roles)
 * consomme par TOUT le module. Le cas est une couche metier au-dessus : les
 * garder distincts evite qu'un import du socle traine tout le domaine.
 */
import { z } from "zod";
import type { CodeTravail, IdCas, IdLieu, IdPatient } from "./partage.js";

/**
 * LES SIX statuts d'etape, releves le 15/08/2026 dans la base VIVE de Courses.
 *
 * La migration `20260716120000_partage_module.sql` de Courses n'en declare que
 * QUATRE — elle est perimee. S'y fier ferait perdre `valide` et `a_corriger`,
 * c'est-a-dire toute la reponse du client : le scenario 3 (le Dr Halfon corrige
 * ou valide un rapport de planification) deviendrait inexprimable.
 */
export const STATUTS_ETAPE = [
  "a_venir",
  "en_cours",
  "terminee",
  "valide",
  "a_corriger",
  "sautee",
] as const;
export type StatutEtape = (typeof STATUTS_ETAPE)[number];

export const STATUTS_CAS = ["demande", "en_cours", "finalise", "archive"] as const;
export type StatutCas = (typeof STATUTS_CAS)[number];

/**
 * R3 — le labo pilote la timeline, le client repond.
 *
 * Regle de Dove, 15/08 : « c'est cote labo qu'on determine le statut et donc
 * qu'on determine si le travail est en attente d'une validation cote client ».
 * `terminee` EST l'etat « le labo a fini, le client doit se prononcer » ;
 * `valide` et `a_corriger` sont la reponse, et n'appartiennent qu'au client.
 *
 * Tant que L5 n'existe pas, aucun client ne peut ecrire : ces deux statuts sont
 * dans le vocabulaire mais inatteignables. C'est voulu — le vocabulaire complet
 * evite une migration de donnees le jour ou l'acces externe arrive.
 */
const STATUTS_REPONSE_CLIENT: readonly StatutEtape[] = ["valide", "a_corriger"];

export function peutPasserLabo(cible: StatutEtape): boolean {
  return !STATUTS_REPONSE_CLIENT.includes(cible);
}

export interface EtapeModele {
  ordre: number;
  libelle: string;
  icone: string;
  description: string;
}

/**
 * Le modele des 5 etapes — version 3 de `app_settings.partage_modele_etapes`
 * dans Courses, relevee le 15/08/2026.
 *
 * LES ACCENTS SONT AJOUTES DELIBEREMENT. Courses stocke `Reception` et
 * `Modelisation` sans accents ; c'est un defaut de saisie, pas une convention.
 * Decision de plan, signalee a Dove le 15/08.
 *
 * Ce modele est RECOPIE dans chaque cas a sa creation (`modele_snapshot`).
 * Le modifier ici ne touche donc AUCUN cas existant — c'est le but : sans ce
 * cliche, changer le modele reecrirait l'histoire d'un dossier que le cabinet
 * a deja lu.
 */
export const MODELE_ETAPES: readonly EtapeModele[] = [
  {
    ordre: 1,
    libelle: "Réception",
    icone: "PackageOpen",
    description: "Empreinte ou fichier reçu au laboratoire",
  },
  {
    ordre: 2,
    libelle: "Modélisation",
    icone: "Box",
    description: "Conception assistée par ordinateur",
  },
  {
    ordre: 3,
    libelle: "Production",
    icone: "Cog",
    description: "Fabrication de la pièce (usinage, stratification, montage)",
  },
  {
    ordre: 4,
    libelle: "Finition",
    icone: "Paintbrush",
    description: "Maquillage, glaçage, contrôle",
  },
  {
    ordre: 5,
    libelle: "Livraison",
    icone: "Truck",
    description: "Expédition vers le cabinet",
  },
];

/* ------------------------------------------------- schemas de frontiere reseau
 *
 * Ils valident ce qui ARRIVE, ou tout est string. Les interfaces de domaine
 * plus bas portent les identifiants nominaux et ne derivent PAS de ces schemas
 * par z.infer : un z.infer rendrait des string nues.
 */

const Uuid = z.string().uuid();

export const InstructionsSchema = z.object({
  typeTravail: z.string().trim().min(1).max(120).optional(),
  teinte: z.string().trim().min(1).max(60).optional(),
  materiau: z.string().trim().min(1).max(120).optional(),
  /** Date ISO seule (`2026-08-15`), jamais un format local. */
  dateSouhaitee: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  remarques: z.string().trim().min(1).max(4000).optional(),
});

export const CasSchema = z.object({
  lieuId: Uuid,
  /** Facultatif — R1 : un cas peut naitre sans fiche rattachee. */
  patientId: Uuid.optional(),
  patientDeclare: z.string().trim().min(1).max(120),
  /** Facultatif — D15 : le fil peut naitre avant le travail Vevidental. */
  travailCode: z.string().trim().min(1).max(60).optional(),
  reference: z.string().trim().min(1).max(60),
  instructions: InstructionsSchema.default({}),
});

/* ------------------------------------------------------- formes de domaine */

export interface Instructions {
  typeTravail: string | null;
  teinte: string | null;
  materiau: string | null;
  dateSouhaitee: string | null;
  remarques: string | null;
}

export interface Etape {
  ordre: number;
  libelle: string;
  icone: string | null;
  description: string | null;
  statut: StatutEtape;
  demarreLe: string | null;
  termineLe: string | null;
}

export interface Cas {
  id: IdCas;
  lieuId: IdLieu;
  /** null = a rapprocher a la main (R1). C'est la file de travail d'Elodie. */
  patientId: IdPatient | null;
  patientDeclare: string;
  travailCode: CodeTravail | null;
  reference: string;
  statut: StatutCas;
  etapeCourante: number;
  instructions: Instructions;
}
